// The messages between a screen and the room server. A room is two seats and
// one match record. The server puts every action in one order and runs the
// rules on it, so the two screens and the server have the same match.
// Added 2026-10-13. Garrett: "a 4 letter room code system that one can
// create via menu, where there is option to create a room and then send that
// link to someone to join and you can play together in real time".

import { ROOM_CODE_LENGTH, ROOM_CODE_LETTERS, ROOMS_PATH } from '../constants';
import type { CasterId } from '../content/casters';
import type { Side } from '../engine/types';
import type { RecordedAction } from '../game/replay';

const CODE_PATTERN = new RegExp(`^[${ROOM_CODE_LETTERS}]{${ROOM_CODE_LENGTH}}$`);

/** The code in what a person typed or the path of a link, in capitals. Null when it is no code. */
export const readRoomCode = (text: string): string | null => {
  const code = text.trim().replace(/^\//, '').replace(/\/$/, '').toUpperCase();
  return CODE_PATTERN.test(code) ? code : null;
};

/** A code drawn from `random`, which gives a number from 0 up to 1. */
export const drawRoomCode = (random: () => number): string =>
  Array.from(
    { length: ROOM_CODE_LENGTH },
    () => ROOM_CODE_LETTERS[Math.floor(random() * ROOM_CODE_LETTERS.length)],
  ).join('');

export const roomSocketPath = (code: string): string => `${ROOMS_PATH}/${code}/socket`;

export type SeatId = 'host' | 'guest';
export const SEAT_IDS: readonly SeatId[] = ['host', 'guest'];
export const otherSeat = (seat: SeatId): SeatId => (seat === 'host' ? 'guest' : 'host');

/** The casters each seat picked, in the order of the picks. */
export type SeatPicks = Record<SeatId, readonly CasterId[]>;

/** A match of the room. The host plays `hostSide`. Each side has the casters its seat picked. */
export type RoomMatchInfo = {
  /** One more for each match of the room. A screen starts a match when it sees a new number. */
  number: number;
  seed: number;
  hostSide: Side;
  casters: Record<Side, readonly CasterId[]>;
};

export type RoomMatch = RoomMatchInfo & { actions: readonly RecordedAction[] };

/** What a screen knows of the room, apart from the actions. */
export type RoomView = {
  code: string;
  /** `lobby`: the seats pick casters. `playing`: a match is on, or over and waiting for a rematch. */
  phase: 'lobby' | 'playing';
  /** A seat has a person, whether or not that person is here now. */
  filled: Record<SeatId, boolean>;
  /** How long the person of a filled seat has been away, in ms. Null while they are here. */
  awayMs: Record<SeatId, number | null>;
  picks: SeatPicks;
  /** The caster each seat reads now in the select, so the other seat sees it. */
  focus: Record<SeatId, CasterId | null>;
  ready: Record<SeatId, boolean>;
  rematch: Record<SeatId, boolean>;
  match: RoomMatchInfo | null;
  spectators: number;
};

export type ClientMessage =
  | { t: 'hello'; token: string; build: string }
  | { t: 'pick'; caster: CasterId }
  | { t: 'random' }
  | { t: 'focus'; caster: CasterId | null }
  | { t: 'ready'; ready: boolean }
  /** `index` is the place the action takes in the record. */
  | { t: 'act'; index: number; action: RecordedAction }
  | { t: 'rematch' }
  /** The other seat is away for `ROOM_CLAIM_AFTER_MS`. Its side gives up. */
  | { t: 'claim' }
  /** A spectator takes a seat whose person is away. */
  | { t: 'sit' }
  /** The screen lost its place in the record. The server sends the whole match. */
  | { t: 'sync' }
  | { t: 'leave' };

export type ServerMessage =
  /** The seat of this screen, or null for a spectator. */
  | { t: 'welcome'; seat: SeatId | null }
  | { t: 'room'; room: RoomView }
  /** The whole match, on a join and after a refused action. */
  | { t: 'sync'; match: RoomMatch | null }
  | { t: 'action'; index: number; action: RecordedAction }
  | { t: 'refused'; reason: string }
  /** The screen runs another build than the server. It must reload. */
  | { t: 'old' }
  /** No room has this code, or it expired. */
  | { t: 'gone' }
  /** The same seat opened in another tab or on another device. */
  | { t: 'replaced' };

/** The side a seat plays in a match. */
export const sideOfSeat = (match: RoomMatchInfo, seat: SeatId): Side =>
  seat === 'host' ? match.hostSide : match.hostSide === 'gold' ? 'violet' : 'gold';
